import React, { useRef } from 'react';
import { Card, Dropdown, Button, Space, Grid, Typography } from 'antd';
import MarkDown from '../Markdown';
import CopyToClipboardButton from '../buttons/CopyToClipboardButton';
import DownloadNoteAsPDFButton from './DownloadNoteAsPDFButton';
import ReviewTranscriptButton from './ReviewTranscriptButton';
import EditButton from './EditButton';
import RegenerateNote from './RegenerateNote';
import { EllipsisOutlined } from '@ant-design/icons';
import ThumbsRating from "../ThumbsRating";
import { useTranslation } from 'react-i18next';
import NoteRuleNudge from './NoteRuleNudge';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const NoteCardView = ({ currentNote, source, handleEditButtonClick, showNudge, handleCloseNudge }) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const noteContentRef = useRef();

  const getNoteContent = () => {
    const sessionDate = source?.getSessionDate();
    const sessionTime = source?.getSessionTime();
    const noteBody = currentNote.getBody();
    
    return {
      display: (
        <div ref={noteContentRef} style={{ marginTop: '40px' }}>
          {sessionDate && sessionTime && (
            <div style={{ marginBottom: '40px' }}>
              <div style={{ marginBottom: '5px' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>{t('common.fields.date')}: </Text>
                <Text style={{ fontSize: '14px' }}>{sessionDate}</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '14px' }}>{t('common.fields.time')}: </Text>
                <Text style={{ fontSize: '14px' }}>{sessionTime}</Text>
              </div>
            </div>
          )}
          <MarkDown content={noteBody} />
        </div>
      ),
      text: sessionDate && sessionTime ? `${t('common.fields.date')}: ${sessionDate}\n${t('common.fields.time')}: ${sessionTime}\n\n${noteBody}` : noteBody
    };
  };

  const embeddedActions = () => {
    if (!source?.isDemo() && currentNote.isValid() && !showNudge) {
      return (
        <div style={{ float: 'right', display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '15px' , marginTop: '-10px'}}>
          <EditButton handleEditButtonClick={handleEditButtonClick} />
          {currentNote.isDoctorNote() && <ReviewTranscriptButton source={source} />}
        </div>
      );
    }
    return null;
  };

  const dropdownMenu = {
    items: [
      {
        key: '2',
        label: currentNote.isDoctorNote() && source.displayRegenerate() && (
          <RegenerateNote sessionUuid={source.getUUID()} disabled={!currentNote.canBeRegenerated()} />
        )
      }
    ].filter(item => item.label) // Only include items that have a label
  };

  const headerContent = (
    <Space size='small'>
      <CopyToClipboardButton textToCopy={getNoteContent().text} disabled={!currentNote.isValid()} />
      <DownloadNoteAsPDFButton note={currentNote} noteContentRef={noteContentRef} />
      {!source.isDemo() && currentNote.isDoctorNote() && currentNote.isValid() && !showNudge && (
        <Dropdown menu={dropdownMenu} trigger={['hover']} placement="bottomRight">
          <Button icon={<EllipsisOutlined />} />
        </Dropdown>
      )}
    </Space>
  );

  return (
    <div>
      <Card
        style={{ 
          borderRadius: '0 10px 10px 10px', 
          borderColor: '#E0E0E0', 
          borderTop: 'none',
          minHeight: screens.lg ? '80vh' : 'auto'
        }}
        bordered={true}
        extra={headerContent}
      >
        {embeddedActions()}
        {showNudge && <NoteRuleNudge onClose={handleCloseNudge} />}
        {getNoteContent().display}
        {!source.isDemo() && currentNote.isValid() && (
          <div style={{display: 'flex', justifyContent: 'left', marginTop: '30px'}}>
            <ThumbsRating
              rateableUuid={currentNote.getUUID()}
              rateableType="Note"
              hint={t('session_details.fields.rating_hint')}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default NoteCardView;
